// @ts-strict-ignore
import { IsThreeJsType } from '@3d-scene/common/util/is-threejs-type';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { Box3, BufferAttribute, BufferGeometry, CircleGeometry, DoubleSide, Material, Mesh, MeshPhongMaterial, Shape, ShapeGeometry, Texture, Vector2, Vector3 } from 'three';
import { ArticleUtils } from '../../../util/article-utils';
import { ArticleVO } from '../../../model/vo/article.vo';
import { ConnectionAreaVO, MetaElementVO, TextureAreaVO } from '@shared/vo/meta-element.vo';
import { ConnectionDefinition } from './connection-configuration';
import { MetaElementUtils } from './meta-element.utils';
import { MeshUtils } from '@3d-scene/common/util/mesh-utils';
import { Injectable } from '@angular/core';
import { ConnectionFactory } from '@3d-scene/model3d/connection.factory';
import { ArticlesCreator } from '@project/components/article/presenter/article-creator';
import { GeometryUtils } from '@3d-scene/common/util/geometry-utils';

export enum ConnectionShapeType {
  RECTANGLE = 'RECTANGLE',
  CIRCLE = 'CIRCLE',
}

export enum ConnectionAreaType {
  TEXTURE_AREA = 'textureArea',
  CONNECTION_AREA = 'connectionArea',
  MULTIPURPOSE_AREA = 'multipurposeArea',
}

export interface ConnectionCreationParameters {
  connectionName: string;
  connectionType: string;
  connectionInfo: string;
  connectionForm: string;
  connectionLength: number;
  connectionWidth: number;
  connectionRadius: number;
  connectionMultiplicity: number;
  connectionDoubleside: boolean;
  bitmapData: HTMLImageElement;
  connectionColor: string;
  position: Vector3;
  rotation: Vector3;
  metaElement: MetaElementVO;
}

@Injectable({ providedIn: 'root' })
export class ConnectionCreator {
  public static readonly MAX_ALLOWED_IMAGE_WIDTH: number = 2048;
  public static readonly MAX_ALLOWED_IMAGE_HEIGHT: number = 2048;
  public static readonly MAX_WIDTH_CONTAINER: number = 300;
  public static readonly MAX_HEIGHT_CONTAINER: number = 200;

  public static readonly INTERNAL_CONNECTION_ROTATION: number = 180;

  constructor(private _connectionFactory: ConnectionFactory) {}

  public createConnection(
    data: ConnectionCreationParameters,
    articleContainer: ArticleContainer,
    article: ArticleVO,
    metaElementExists: boolean = false
  ): Connection {
    const mainConnectionDefinition: ConnectionDefinition = this.getMainConnectionDefinition(
      data.connectionType,
      data.connectionInfo,
      data.connectionName
    );
    const mainResultData: { meta: MetaElementVO; connection: Connection } = metaElementExists
      ? this.continueCreateConnection(mainConnectionDefinition, data, articleContainer, article, data.metaElement)
      : this.continueCreateConnection(mainConnectionDefinition, data, articleContainer, article);
    const mainMetaElement: MetaElementVO = mainResultData.meta;
    if (data.metaElement) {
      mainMetaElement.corridor = data.metaElement.corridor;
    }
    this.updateMainMetaElementPropertiesByParameters(mainMetaElement, data);
    if (mainMetaElement) {
      const internalConnectionDefinition: ConnectionDefinition = this.getInternalConnectionDefinition(
        data.connectionType,
        data.connectionInfo,
        mainMetaElement.name
      );
      if (internalConnectionDefinition) {
        const internalResultData: { meta: MetaElementVO; connection: Connection } = this.continueCreateConnection(
          internalConnectionDefinition,
          data,
          articleContainer,
          article
        );
        const internalMetaElement: MetaElementVO = internalResultData.meta;
        internalMetaElement.orderNumber = mainMetaElement.orderNumber;
        internalMetaElement.corridor = mainMetaElement.corridor;
        MetaElementUtils.applySetIdToMultipurposeAreas(mainMetaElement, internalMetaElement);
      }
    }
    this.updateConnectionPositionByParameters(mainResultData.connection, data);

    return mainResultData.connection;
  }

  private updateMainMetaElementPropertiesByParameters(mainMetaElement: MetaElementVO, parameters: ConnectionCreationParameters): void {
    const parametersMetaElement: MetaElementVO = parameters.metaElement;
    if (parametersMetaElement && mainMetaElement) {
      mainMetaElement.behaviorProperties = parametersMetaElement.behaviorProperties.map((property) => property.clone());
      if (mainMetaElement instanceof ConnectionAreaVO && parametersMetaElement instanceof ConnectionAreaVO) {
        mainMetaElement.connectionFeatures = parametersMetaElement.connectionFeatures.map((property) => property.clone());
        mainMetaElement.connectionRequirements = parametersMetaElement.connectionRequirements.map((property) => property.clone());
        mainMetaElement.connectionPlausiProperties = parametersMetaElement.connectionPlausiProperties.map((property) => property.clone());
        mainMetaElement.connectionConstructionProperties = parametersMetaElement.connectionConstructionProperties.map((property) => property.clone());
      }
    }
  }

  private updateConnectionPositionByParameters(mainConnection: Connection, parameters: ConnectionCreationParameters): void {
    if (!mainConnection || (!parameters.position && !parameters.rotation)) {
      return;
    }
    const position: Vector3 = parameters.position || mainConnection.meshPosition;
    const rotation: Vector3 = parameters.rotation || mainConnection.meshRotationDegree;
    const widthOrRadius: number = mainConnection.meshIsCircle ? mainConnection.meshRadius : mainConnection.meshWidth;
    const length: number = mainConnection.meshIsCircle ? null : mainConnection.meshLength;

    const linkedConnections: Connection[] = this.getMultipurposeConnectionsBySelection(mainConnection);
    for (const linkedConnection of linkedConnections) {
      const meshRotation: Vector3 = this.applyAdditionalConnectionRotation(linkedConnection, rotation);
      linkedConnection.updateMesh(position, meshRotation, widthOrRadius, length);
    }
  }

  /**
   * Switches connectionName from main to internal and vise versa.
   */
  public toggleConnectionName(connectionName: string): string {
    if (MetaElementUtils.hasInternalConnectionPostfix(connectionName)) {
      return MetaElementUtils.removeInternalConnectionPostfix(connectionName);
    } else {
      return MetaElementUtils.applyInternalConnectionPostfix(connectionName);
    }
  }

  private continueCreateConnection(
    connectionDefinition: ConnectionDefinition,
    data: ConnectionCreationParameters,
    articleContainer: ArticleContainer,
    article: ArticleVO,
    existingMetaElement?: MetaElementVO
  ): { meta: MetaElementVO; connection: Connection } {
    if (!connectionDefinition) {
      return null;
    }

    if (connectionDefinition.isInternal) {
      // in case of undo/redo operation main-connection could contain "_internal" postfix
      // this should be kept in such case, but postfix must be removed from the internal connection then
      connectionDefinition.name = this.toggleConnectionName(connectionDefinition.name);
    }

    const meta: MetaElementVO = existingMetaElement
      ? existingMetaElement
      : MetaElementUtils.createMetaElementByName(article, connectionDefinition, data.connectionMultiplicity, data.connectionDoubleside);

    let mesh: Mesh;
    switch (data.connectionForm) {
      case ConnectionShapeType.RECTANGLE:
        mesh = this.createRectangleConnection(data.connectionLength, data.connectionWidth, articleContainer);
        break;
      case ConnectionShapeType.CIRCLE:
        mesh = this.createCircleConnection(data.connectionRadius, articleContainer);
        break;
    }

    if (data.bitmapData) {
      const material: MeshPhongMaterial = new MeshPhongMaterial();
      const texture: Texture = new Texture();
      texture.image = data.bitmapData as HTMLImageElement;
      texture.needsUpdate = true;
      material.map = texture;
      mesh.material = material;
      mesh.material.needsUpdate = true;
    } else {
      mesh.material = this.createColorMaterial(data.connectionColor);
    }

    if (this.isBothSidesConnection(meta)) {
      (mesh.material as MeshPhongMaterial).side = DoubleSide;
    }
    if (connectionDefinition.isInternal) {
      mesh.visible = false;
    }
    mesh.name = connectionDefinition.name;

    if (connectionDefinition.isInternal) {
      MeshUtils.rotateByAxis(mesh, MathUtils.toRadians(ConnectionCreator.INTERNAL_CONNECTION_ROTATION), new Vector3(0, 0, 1));
    }

    let connection: Connection = this._connectionFactory.getNewInstance(articleContainer, mesh);
    articleContainer.setConnection(connection);
    articleContainer.addChildToModel(mesh);
    connection = articleContainer.findConnection(connectionDefinition.name); // Must be done in this way, because addChildToModel(mesh) can overwrite the connection in articleContainer
    connection.texturesEnabled = false;
    connection.metaElementVO = meta;
    connection.metaElementUsage = null;
    mesh.geometry.computeBoundingBox();
    mesh.geometry.computeBoundingSphere();

    if (!existingMetaElement) {
      MetaElementUtils.addMetaElementToArticle(article, meta);
    }

    return { meta, connection };
  }

  public getConnectionShapeType(connection: Connection): ConnectionShapeType {
    return connection.meshIsCircle ? ConnectionShapeType.CIRCLE : ConnectionShapeType.RECTANGLE;
  }

  public createRectangleConnection(length: number, width: number, articleContainer: ArticleContainer): Mesh {
    const box: Box3 = ArticleContainerUtils.calculateBBox(articleContainer.model);
    const widthValue: number = length;
    const depthValue: number = width;
    const heightValue: number = box.max.z;
    const p1: Vector3 = new Vector3(widthValue / -2, depthValue / -2, heightValue);
    const p2: Vector3 = new Vector3(widthValue / 2, depthValue / -2, heightValue);
    const p3: Vector3 = new Vector3(widthValue / 2, depthValue / 2, heightValue);
    const p4: Vector3 = new Vector3(widthValue / -2, depthValue / 2, heightValue);
    const connectionRectangle: Rectangle3D = Rectangle3D.fromArray([p1, p2, p3, p4]);
    return ArticlesCreator.createPlaneForRectangle(connectionRectangle);
  }

  public static createCustomShapeConnection(shapeConnectionPoints: Vector2[], articleContainer: ArticleContainer): Mesh {
    if (!shapeConnectionPoints || shapeConnectionPoints.length < 3) {
      return null;
    }
    const shape: Shape = new Shape(shapeConnectionPoints);
    const geometry: ShapeGeometry = new ShapeGeometry(shape);
    this.applyDefaultUVsBasedOnBoundingBox(geometry);
    GeometryUtils.setNeedsUpdate(geometry);
    geometry.computeVertexNormals();
    const mesh: Mesh = new Mesh(geometry);
    return mesh;
  }

  private static applyDefaultUVsBasedOnBoundingBox(geometry: BufferGeometry): void {
    geometry.computeBoundingBox();
    const bbox: Box3 = geometry.boundingBox;
    const bboxSize: Vector3 = new Vector3();
    bbox.getSize(bboxSize);
    const faces = GeometryUtils.getFaces(geometry);
    const vertices = GeometryUtils.getVertices(geometry);
    const uvs: Vector2[] = [];
    for (const face of faces) {
      const a: Vector3 = vertices[face.a];
      const b: Vector3 = vertices[face.b];
      const c: Vector3 = vertices[face.c];
      const uva: Vector2 = new Vector2((a.x - bbox.min.x) / bboxSize.x, (a.y - bbox.min.y) / bboxSize.y);
      const uvb: Vector2 = new Vector2((b.x - bbox.min.x) / bboxSize.x, (b.y - bbox.min.y) / bboxSize.y);
      const uvc: Vector2 = new Vector2((c.x - bbox.min.x) / bboxSize.x, (c.y - bbox.min.y) / bboxSize.y);
      uvs.push(uva, uvb, uvc);
    }
    const uvAttribute = new BufferAttribute(new Float32Array(uvs.flatMap((v) => [v.x, v.y])), 2);
    uvAttribute.needsUpdate = true;
    geometry.setAttribute('uv', uvAttribute);
  }

  public createCircleConnection(radius: number, articleContainer: ArticleContainer): Mesh {
    const box: Box3 = ArticleContainerUtils.calculateBBox(articleContainer.model);
    const circleGeometry: CircleGeometry = new CircleGeometry(radius, 32, Math.PI, Math.PI * 2);
    circleGeometry.translate(0, 0, box.max.z);
    return new Mesh(new BufferGeometry().copy(circleGeometry));
  }

  public getMainConnectionDefinition(connectionType: string, connectionInfo: string, connectionName: string = null): ConnectionDefinition {
    const connectionDefinition: ConnectionDefinition = this.isMultipurposeAreaType(connectionType)
      ? new ConnectionDefinition(ConnectionAreaType.CONNECTION_AREA, ConnectionAreaVO.SIDE_ACCEPTING)
      : new ConnectionDefinition(connectionType, connectionInfo);
    connectionDefinition.name = connectionName;
    return connectionDefinition;
  }

  public getInternalConnectionDefinition(connectionType: string, connectionInfo: string, connectionName: string = null): ConnectionDefinition {
    const connectionDefinition: ConnectionDefinition = this.isMultipurposeAreaType(connectionType)
      ? new ConnectionDefinition(ConnectionAreaType.TEXTURE_AREA, TextureAreaVO.TYPE_TEXT_OR_IMAGE_USAGE)
      : null;
    if (connectionDefinition) {
      connectionDefinition.name = connectionName;
      connectionDefinition.isInternal = true;
    }
    return connectionDefinition;
  }

  public getConnectionDefinitionByMetaElement(metaElement: MetaElementVO, considerMultipurposeAreaType: boolean): ConnectionDefinition {
    if (!metaElement) {
      return null;
    }
    const connectionDefinition: ConnectionDefinition = new ConnectionDefinition();
    connectionDefinition.type = this.getConnectionTypeByMetaElement(metaElement, considerMultipurposeAreaType);
    connectionDefinition.subtype = this.getConnectionInfoByMetaElement(metaElement, considerMultipurposeAreaType);
    connectionDefinition.name = metaElement.name;
    connectionDefinition.isInternal = metaElement.isInternal;
    return connectionDefinition;
  }

  public updateSelectedConnectionMesh(
    selectedConnection: Connection,
    position: Vector3,
    rotation: Vector3,
    widthOrRadius: number,
    length: number
  ): void {
    const connectionsToUpdate: Connection[] = this.getMultipurposeConnectionsBySelection(selectedConnection);
    connectionsToUpdate.forEach((c: Connection) => {
      const meshRotation: Vector3 = this.applyAdditionalConnectionRotation(c, rotation);
      c.updateMesh(position, meshRotation, widthOrRadius, length);
    });
  }

  public applyAdditionalConnectionRotation(connection: Connection, rotation: Vector3): Vector3 {
    let meshRotation: Vector3 = rotation;
    if (connection.metaElementVO && connection.metaElementVO.isInternal) {
      meshRotation = this.addInternalRotation(connection, rotation);
    }
    return meshRotation;
  }

  public addInternalRotation(connection: Connection, rotation: Vector3): Vector3 {
    const additionalRotation: Vector3 = connection.meshNormalizedVector.multiplyScalar(ConnectionCreator.INTERNAL_CONNECTION_ROTATION);
    return rotation.clone().add(additionalRotation);
  }

  public getMultipurposeConnectionsBySelection(selectedConnection: Connection): Connection[] {
    if (!selectedConnection) {
      return [];
    }
    const areasInSet: MetaElementVO[] = this.getMultipurposeAreasBySelection(selectedConnection);
    const connections: Connection[] = [];
    areasInSet.forEach((metaElement: MetaElementVO) => connections.push(selectedConnection.articleContainer.findConnectionByName(metaElement.name)));
    return connections;
  }

  public getMultipurposeAreasBySelection(selectedConnection: Connection): MetaElementVO[] {
    if (!selectedConnection) {
      return [];
    }
    return this.getMultipurposeAreasByConnectionName(selectedConnection.connectionName, selectedConnection.article);
  }

  public getMultipurposeAreasByConnectionName(connectionName: string, article: ArticleVO): MetaElementVO[] {
    if (!connectionName || !article) {
      return [];
    }

    const linkedMetaElements: MetaElementVO[] = [];
    const metaElement: MetaElementVO = ArticleUtils.findMetaElementByName(article, connectionName);
    if (metaElement) {
      linkedMetaElements.push(metaElement);
    }
    const linkedMetaElementName = connectionName.endsWith(MetaElementUtils.INTERNAL_CONNECTION_POSTFIX)
      ? MetaElementUtils.removeInternalConnectionPostfix(connectionName)
      : MetaElementUtils.applyInternalConnectionPostfix(connectionName);
    const connectedMetaElement: MetaElementVO = ArticleUtils.findMetaElementByName(article, linkedMetaElementName);
    if (connectedMetaElement) {
      linkedMetaElements.push(connectedMetaElement);
    }

    return linkedMetaElements;
  }

  public getConnectionTypeByMetaElement(metaElement: MetaElementVO, considerMultipurposeAreaType: boolean = true): string {
    if (considerMultipurposeAreaType && MetaElementUtils.isMultipurposeArea(metaElement)) {
      return ConnectionAreaType.MULTIPURPOSE_AREA;
    }
    if (metaElement instanceof ConnectionAreaVO) {
      return ConnectionAreaType.CONNECTION_AREA;
    }
    if (metaElement instanceof TextureAreaVO) {
      return ConnectionAreaType.TEXTURE_AREA;
    }
    return null;
  }

  public findMetaElementByConnectionType(
    sourceMetaElements: MetaElementVO[],
    connectionType: string,
    considerMultipurposeAreaType: boolean = true
  ): MetaElementVO {
    if (!sourceMetaElements || !connectionType) {
      return null;
    }
    for (const metaElement of sourceMetaElements) {
      const metaElementType: string = this.getConnectionTypeByMetaElement(metaElement, considerMultipurposeAreaType);
      if (metaElementType === connectionType) {
        return metaElement;
      }
    }
    return null;
  }

  public isConnectionAreaType(areaType: string): boolean {
    return areaType === ConnectionAreaType.CONNECTION_AREA;
  }

  public isTextureAreaType(areaType: string): boolean {
    return areaType === ConnectionAreaType.TEXTURE_AREA;
  }

  public isMultipurposeAreaType(areaType: string): boolean {
    return areaType === ConnectionAreaType.MULTIPURPOSE_AREA;
  }

  public getConnectionInfoByMetaElement(metaElement: MetaElementVO, considerMultipurposeAreaType: boolean = true): string {
    if (considerMultipurposeAreaType && MetaElementUtils.isMultipurposeArea(metaElement)) {
      return null;
    }
    if (metaElement instanceof ConnectionAreaVO) {
      return metaElement.connectionSide;
    }
    if (metaElement instanceof TextureAreaVO) {
      return metaElement.textureType;
    }
    return null;
  }

  public isBothSidesConnection(metaElement: MetaElementVO): boolean {
    return metaElement instanceof ConnectionAreaVO ? metaElement.bothsides : false;
  }

  public createColorMaterial(color: string, alpha: number = 1): MeshPhongMaterial {
    return new MeshPhongMaterial({ color, opacity: alpha });
  }

  public applyImageToConnection(image: HTMLImageElement, connection: Connection): void {
    const texture: Texture = new Texture();
    texture.image = image;
    texture.needsUpdate = true;
    const connectionDefaultMaterial: Material = connection.getDefaultMaterial();
    if (IsThreeJsType.meshPhongMaterial(connectionDefaultMaterial)) {
      connectionDefaultMaterial.map = image ? texture : null;
      connectionDefaultMaterial.needsUpdate = true;
    }
  }
}
